import { Box } from "@itandi/itandi-bb-ui/Box";
import React from "react";
import { ScheduleInputNavigation } from "~/components/common/construction_schedule/ScheduleInputNavigation";
import { ConstructionVendorNavigation } from "~/components/common/constructions/navigation/ConstructionVendorNavigation";
import { ReportImageNavigation } from "~/components/common/reports/navigation/ReportImageNavigation";
import { StatusHandleVendorButtons } from "~/components/common/statuses/StatusHandleVendorButtons";
import { UploadFileNavigation } from "~/components/common/upload_files/UploadFileNavigation";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { useDealCompletionReportImages } from "~/hooks/vendors/deals/useDealCompletionReportImages";
import { useDealEstimationHeaders } from "~/hooks/vendors/deals/useDealEstimationHeaders";
import { useDealEstimationHistories } from "~/hooks/vendors/deals/useDealEstimationHistories";
import { useDealEstimations } from "~/hooks/vendors/deals/useDealEstimations";
import { useDealInspectionReportImages } from "~/hooks/vendors/deals/useDealInspectionReportImages";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useSessions } from "~/hooks/vendors/useSessions";
import type { NskResponse } from "~/models/Api";
import { ESTIMATION_BY, matchDealType } from "~/models/Deal";
import type { DealConstructionScheduleEdit } from "~/models/DealConstructionSchedule";
import type { DealUploadFile } from "~/models/DealUploadFile";
import { isOriginVendor } from "~/models/DealVendorCompany";
import {
	AWAITING_ACCEPTANCE,
	IN_CONSTRUCTION,
	SEND_ESTIMATE,
	statusMovingValidation
} from "~/models/Status";
import { PATH as COMPLETION_REPORT_PATH } from "~/pages/vendors/deals/[id]/completion_reports";
import { PATH as INSPECTION_REPORT_PATH } from "~/pages/vendors/deals/[id]/inspection_reports";
import { getFilePath as getVendorFilePath } from "~/utils/api/vendors//deals/DealUploadedFile";
import { create as createDealConstructionSchedule } from "~/utils/api/vendors/DealConstructionSchedule";
import {
	getDownloadPath,
	create as uploadReceiptFile
} from "~/utils/api/vendors/deals/DealReceiptFile";
import { destroy as destroyReceiptFile } from "~/utils/api/vendors/deals/DealUploadedFile";
import Styles from "./statuses.module.scss";

export const InConstruction: React.FC = () => {
	const dealId = useIdQueryHandler();
	const { data: deal, mutate: mutateDeal } = useDeals().show(dealId);
	const { data: estimationContext } = useDealEstimations().index(dealId);
	const { data: dealEstimationHistoryContext } =
		useDealEstimationHistories().index(dealId);
	const { data: dealEstimationHeaders } =
		useDealEstimationHeaders().index(dealId);
	const requestHeader =
		dealEstimationHeaders?.find((header) => header.status === "request") ??
		null;
	const { data: inspectionImages } =
		useDealInspectionReportImages().index(dealId);
	const { data: completionImages } =
		useDealCompletionReportImages().index(dealId);
	const { data: session } = useSessions().index();
	const handleMutateDeal = async (): Promise<void> => {
		await mutateDeal?.();
	};
	const forwardStatusEnabled = React.useMemo(() => {
		if (
			deal &&
			deal.estimationBy === ESTIMATION_BY.COMPANY &&
			completionImages &&
			completionImages.length > 0
		)
			return true;
		if (
			deal === null ||
			inspectionImages === null ||
			completionImages === null ||
			estimationContext === null ||
			dealEstimationHistoryContext === null
		)
			return true;
		return statusMovingValidation[IN_CONSTRUCTION](
			deal,
			estimationContext,
			dealEstimationHistoryContext,
			requestHeader,
			inspectionImages,
			completionImages,
			false
		);
	}, [
		completionImages,
		deal,
		dealEstimationHistoryContext,
		estimationContext,
		inspectionImages,
		requestHeader
	]);
	if (
		deal === null ||
		session === null ||
		dealId === null ||
		inspectionImages === null ||
		completionImages === null
	) {
		return null;
	}
	const handleSubmitDealConstructionSchedule = async (
		schedules: DealConstructionScheduleEdit[]
	): Promise<NskResponse> => createDealConstructionSchedule(deal.id, schedules);
	const handleDestroyReceipt = async (
		file: DealUploadFile
	): Promise<NskResponse> => destroyReceiptFile(deal.id, file.id);
	const handleUploadReceipt = async (file: File): Promise<NskResponse> =>
		uploadReceiptFile(file, deal.id);
	const handleCreateReceipt = async (): Promise<NskResponse> =>
		uploadReceiptFile(null, deal.id);
	const handleGetReceiptPath = (file: DealUploadFile): string =>
		getVendorFilePath(deal.id, file);
	const showInspectionImagePanel =
		matchDealType(deal, "repair") && !deal.conductFieldSurvey;
	const downloadExcel = (): void => {
		const path = getDownloadPath(deal.id);
		window.open(path);
	};
	return (
		<div>
			<div>
				<h3 className={Styles.Title}>施工項目が確定しました</h3>
				<Box marginY={16}>
					<p className={Styles.Description}>
						全ての施工項目が確定したことを確認してください。
						<br />
						工事完了後、
						{deal.completionReportImageRequired
							? "完了報告画像を登録することで、"
							: null}
						ステータスを進めることができます。
					</p>
				</Box>
			</div>
			<ConstructionVendorNavigation title="施工項目が確定しました" />
			{isOriginVendor(deal, session.vendorCompany.id) && (
				<UploadFileNavigation
					deal={deal}
					description="登録されている工事発注書・請書は必要に応じてお手続きにご利用ください"
					downloadExcel={downloadExcel}
					files={deal.dealReceiptFiles}
					fileType="工事発注書・請書"
					handleCreate={handleCreateReceipt}
					handleDestroy={handleDestroyReceipt}
					handleGetFilePath={handleGetReceiptPath}
					handleMutate={handleMutateDeal}
					handleUpload={handleUploadReceipt}
					isVendor
					title="工事発注書・請書を登録してください"
				/>
			)}
			<ScheduleInputNavigation
				deal={deal}
				handleMutate={handleMutateDeal}
				handleSubmit={handleSubmitDealConstructionSchedule}
			/>
			{showInspectionImagePanel ? (
				<ReportImageNavigation
					detailPageButtonLabel="画像登録画面"
					detailPagePath={INSPECTION_REPORT_PATH(dealId)}
					reportImages={inspectionImages}
					title="施工前の画像を登録してください"
				/>
			) : null}
			<ReportImageNavigation
				detailPageButtonLabel="画像登録画面"
				detailPagePath={COMPLETION_REPORT_PATH(dealId)}
				optionalLabel={!deal.completionReportImageRequired}
				reportImages={completionImages}
				title="完了報告画像を登録してください"
			/>
			{isOriginVendor(deal, session.vendorCompany.id) && (
				<StatusHandleVendorButtons
					forwardDisabled={!forwardStatusEnabled}
					nextStatusId={AWAITING_ACCEPTANCE}
					prevStatusId={SEND_ESTIMATE}
				/>
			)}
		</div>
	);
};
